import { GenericAbortSignal } from 'axios'
import { API_APPLICANT_URL } from '../constants/base-urls'
import type { IDataTableCandidateApplicant, IPaginationParam, IPaginationResponse, IVacancy } from '../types'
import { createAxiosInstance } from '../utils'

const axios = createAxiosInstance({
  baseURL: API_APPLICANT_URL,
  withAuth: true,
})

const axiosTaekwang = createAxiosInstance({
  baseURL: API_APPLICANT_URL,
  withAuth: true,
})

type FetchCandidateParams = {
  education?: string
  position?: string
  province?: string
  vacancyId?: string
  isVideo?: string
  nik?: number
  candidateStatus?: string
  search?: string
  candidateName?: string
  startDate?: string
  endDate?: string
} & IPaginationParam

export const fetchBlacklist = (params?: FetchCandidateParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IDataTableCandidateApplicant> }>(`/applicant/blacklist`, { params, signal })
    .then((response) => response.data.data)
}

export const unblacklist = (id: string) => {
  return axiosTaekwang.patch(`/applicant/blacklist/${id}`).then((response) => response.data.data)
}

export const createBlacklist = (payload: Record<string, any>) => {
  return axios.post<{ data: IDataTableCandidateApplicant }>(`/applicant/blacklist`, payload).then((response) => response.data.data)
}

export const apply = (payload: Record<string, any>) => {
  return axios.post<{ data: IDataTableCandidateApplicant }>(`/applicant/apply`, payload).then((response) => response.data.data)
}

export const offer = (payload: Record<string, any>) => {
  return axios.post<{ data: IDataTableCandidateApplicant }>(`/applicant/offer`, payload).then((response) => response.data.data)
}

export const rejectOffer = (payload: Record<string, any>) => {
  return axios.patch<{ data: IDataTableCandidateApplicant }>(`/applicant/reject-offer`, payload).then((response) => response.data.data)
}

export const fetchOffer = (params?: FetchCandidateParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IDataTableCandidateApplicant> }>(`/applicant/offer`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchCandidateManagement = (params?: FetchCandidateParams, signal?: GenericAbortSignal) => {
  return axiosTaekwang
    .get<{ data: IPaginationResponse<IDataTableCandidateApplicant> }>(`/applicant`, { params, signal })
    .then((response) => response.data.data)
    .catch((error) => {
      console.error('Error fetching candidate management:', error)
    })
}

export const createShortlist = (payload: Record<string, any>) => {
  return axiosTaekwang.post<{ data: IDataTableCandidateApplicant }>(`applicant/shortlist`, payload).then((response) => response.data.data)
}

export const deleteShortlist = (payload: Record<string, any>) => {
  return axiosTaekwang.delete('applicant/shortlist', { data: payload }).then((response) => response.data.data)
}

export const fetchShortlist = (params?: FetchCandidateParams, signal?: GenericAbortSignal) => {
  return axiosTaekwang
    .get<{ data: IPaginationResponse<IDataTableCandidateApplicant> }>(`/applicant/shortlist`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchWithdraw = (params?: FetchCandidateParams, signal?: GenericAbortSignal) => {
  return axios
    .get(`/applicant/withdraw`, {
      params,
      signal,
    })
    .then((response) => response.data.data)
}

export const withdraw = (payload: Record<string, any>) => {
  return axios.patch<{ data: IDataTableCandidateApplicant }>(`/applicant/withdraw`, payload).then((response) => response.data.data)
}

export const reject = (payload: Record<string, any>) => {
  return axios.patch<{ data: IDataTableCandidateApplicant }>(`/applicant/reject-apply`, payload).then((response) => response.data.data)
}

export const fetchReject = (params?: FetchCandidateParams, signal?: GenericAbortSignal) => {
  return axiosTaekwang
    .get<{ data: IPaginationResponse<IDataTableCandidateApplicant> }>(`/applicant/reject-apply`, { params, signal })
    .then((response) => response.data.data)
}

export const unreject = (candidateId: string, employeeId: string) => {
  return axiosTaekwang
    .patch(
      `/applicant/unreject/${candidateId}`,
      {},
      {
        headers: {
          employeeId: employeeId,
        },
      },
    )
    .then((response) => response.data.data)
}

export const moveToAnotherVacancy = (payload: Record<string, any>) => {
  return axios
    .post<{ data: { warning: { type: string; message: string } | null } }>(`/applicant/move-to-another-vacancy`, payload)
    .then((response) => response.data.data)
}

export const applyVacancy = (payload: Record<string, any>) => {
  return axiosTaekwang
    .post<{ data: { warning: { type: string; message: string } | null } }>(`/basic/apply`, payload)
    .then((response) => response.data.data)
}

export const applyVacancyCareersite = (vacancyId: string) => {
  return axiosTaekwang
    .post<{ data: { warning: { type: string; message: string } | null } }>(`/careersite/apply`, {
      vacancyId: vacancyId,
    })
    .then((response) => response.data.data)
}

export const fetchVacanciesCandidate = (candidateId: string) => {
  return axiosTaekwang.get<{ data: IPaginationResponse<IVacancy> }>(`/vacancy/${candidateId}`).then((response) => response.data.data)
}
// vacancy?isRequisition=0&status=active

export const fetchVacanciesCandidateXplore = (id: string) => {
  return axios.get<{ data: IPaginationResponse<IVacancy> }>(`applicant/vacancy/offer/${id}`).then((response) => response.data.data)
}

export const fetchVacanciesCandidateXploreWithParams = (id: string, params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IVacancy> }>(`applicant/vacancy/offer/${id}`, { params, signal })
    .then((response) => response.data.data)
}

export const sendReminder = (applicanId: string) => {
  return axios.patch(`applicant/offer/remind?applicantId=${applicanId}`)
}

export const fetchApplicantCard = (params?: FetchCandidateParams, signal?: GenericAbortSignal) => {
  return axiosTaekwang
    .get<{ data: IPaginationResponse<IVacancy> }>(`/applicant/card`, { params, signal })
    .then((response) => response.data.data)
}

export const updateHRPic = (applicantId: string, payload: { employeeId: string; isPhoneCalled: boolean }) => {
  return axiosTaekwang.put(`/applicant/update-hr-pic/${applicantId}`, payload).then((r) => r.data?.data)
}
